import {
    Badge, Button, Card, Col, DatePicker, Divider, Drawer,
    Form, Input, Modal, Popover, Row, Select, Space, Switch, Tooltip, Typography
} from 'antd';
import { EditOutlined, CheckOutlined, CloseOutlined, ClockCircleOutlined, ExclamationCircleOutlined, FileExcelOutlined, FilePdfOutlined, PrinterOutlined, DeleteOutlined, LinkOutlined } from '@ant-design/icons';
import { useEffect, useState, useRef } from 'react';
import GenericTable from '../../../components/Commons/Table/generic-table.component';
import { useMutation, useQuery } from "@tanstack/react-query";
import { getAll, remove, update, getColumns, updateColumns } from '../../../services/bookingServices.service';
import { getAll as loadServiceTypes } from '../../../services/serviceTypes.service';
import { getAll as loadServiceStates } from '../../../services/serviceStates.service';
import { getAll as loadVehicles } from '../../../services/vehicles.service';
import { getAll as loadWorkers } from '../../../services/workers.service';
import { all as loadZones } from '../../../services/zones.service';
import { getAll as loadLocations } from '../../../services/locations.service';
import ProfilePermissionService from '../../../services/profilePermissions.service';
import { AlertService } from '../../../services/alert.service';
import ShowDataLength from '../../../components/Commons/Table/results-length-table.component';
import moment from 'moment';
import { RangePickerProps } from 'antd/lib/date-picker';
import DisplayAlert from '../../../components/Commons/Alert';
import { cutLongText, downloadExcel, formatCurrency, limitServicesToExportPdf } from '../../../services/utils';
import { PaginationProps } from 'antd/lib';
import { FilterValue } from 'antd/es/table/interface';
import FormService from './Form';
import ExportBtn from '../../../components/Commons/Export/ExportButton';
import ConfirmModal from '../../../components/Commons/Modal/confirm-modal.component';
import { usePDF } from "@react-pdf/renderer";
import ExportServicesPDF from './ExportServicesPDF';
import ShowHistoric from './ShowHistoric';
import dayjs from 'dayjs';
import 'dayjs/locale/pt';
import { isMobile } from 'react-device-detect';
import FilterButton from '../../../components/Commons/Table/FilterButton';
import { Link } from 'react-router-dom';

import type { InputRef } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import SearchInput from '../../../components/Commons/Input/SearchInput';
const { Text } = Typography;
const { Search } = Input;

export default function ConsultServices() {
    const [services, setServices] = useState<any[]>([]);
    const [currentPage, setCurrentPage] = useState<number | undefined>(1);
    const [perPage, setPerPage] = useState<number | undefined>(10);
    const [total, setTotal] = useState<number>(0);
    const [order, setOrder] = useState<string>('hour-asc');
    const [search, setSearch] = useState<string>('');
    const [startDate, setStartDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState<string>(dayjs().format('YYYY-MM-DD'));
    const [serviceType, setServiceType] = useState<(number | string)[]>([]);
    const [pickup, setPickup] = useState<string | number | undefined>(undefined);
    const [dropoff, setDropoff] = useState<string | number | undefined>(undefined);
    const [servicesToExport, setServicesToExport] = useState<any[]>([]);
    const [selectColumns, setSelectColumns] = useState<string[]>([]);
    const [selectColumnsChanged, setSelectColumnsChanged] = useState<boolean>(false);
    const [openHistoric, setOpenHistoric] = useState<boolean>(false);
    const [historic, setHistoric] = useState<any[]>([]);
    const [openExportOptions, setOpenExportOptions] = useState<boolean>(false);
    const colorBtns = { color: '#000000aa' }
    const colorBtnsDisabled = { color: '#FF0000aa' }
    const [changeVerified, setChangeVerified] = useState<{ value: boolean; id: number } | undefined>(undefined);

    const [openServiceModalForm, setOpenServiceModalForm] = useState<boolean>(false);
    const [serviceToEdit, setServiceToEdit] = useState<any>();
    const [removeService, setRemoveService] = useState<any | undefined>(undefined);
    const [modalFormTitle, setModalFormTitle] = useState<string>('');

    const [pdfInstante, updatePdfInstante] = usePDF({
        document: <ExportServicesPDF services={servicesToExport} filters={{ start_date: startDate, end_date: endDate }} />
    });

    type ColumnFilters = {
        [key: string]: any;
        onlyWorkerType?: 'STAFF' | 'SUPPLIERS';
    };

    type DynamicServiceColorsProps = {
        dataSource: any[];
    };

    const DynamicServiceColors: React.FC<DynamicServiceColorsProps> = ({ dataSource }) => {
        useEffect(() => {
            let styleEl = document.getElementById('dynamic-service-colors') as HTMLStyleElement | null;
            if (!styleEl) {
                styleEl = document.createElement('style');
                styleEl.id = 'dynamic-service-colors';
                document.head.appendChild(styleEl);
            }
            // Limpa o conteúdo antigo
            styleEl.innerHTML = '';
            // Gera novas regras
            const rules = Array.from(new Set(
                dataSource
                    .map(r => r?.serviceType?.data?.color)
                    .filter(Boolean)
                    .map((color: string) => {
                        const className = `.service-type-color-${color.replace('#', '')}`;
                        return `${className} { background-color: ${color} !important; }`;
                    })
            )).join('\n');
            styleEl.innerHTML = rules;
            // Cleanup quando sair da página
            return () => {
                styleEl && (styleEl.innerHTML = '');
            };
        }, [dataSource]);
        return null;
    };

    const [formSearch] = Form.useForm();
    const [formFilter] = Form.useForm();
    const [formSelectColumns] = Form.useForm();
    const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});

    const profilePermissionService = ProfilePermissionService.getInstance();

    const settings = {
        edit: { title: 'Editar serviço' }
    };

    const { isLoading: servicesLoading, refetch: loadServices } = useQuery(
        ['loadServices', currentPage, search, order, perPage, startDate, endDate, serviceType, columnFilters, pickup, dropoff],
        () => getAll({  
            page: currentPage,
            per_page: perPage,
            order,
            search,
            withHistoric: '1',
            start_date: startDate,
            end_date: endDate,
            service_type_id: serviceType,
            pickup_location_id: pickup,
            dropoff_location_id: dropoff,
            only_worker_type: columnFilters.onlyWorkerType, // envia snake_case
            ...Object.fromEntries(Object.entries(columnFilters).filter(([k]) => k !== 'onlyWorkerType'))
        }),
        {
            onSuccess: response => {
                setServices(response.data.map((service: any) => {
                    return {
                        key: service.data.id,
                        ...service.data,
                        ...service.relationships,
                        operator_name: service?.relationships?.booking?.relationships?.operator?.data?.name || ''
                    }
                }));

                setServicesToExport([]);
                setCurrentPage(response.meta.current_page);
                setPerPage(response.meta.per_page);
                setTotal(response.meta.total);
            },
            refetchOnWindowFocus: false
        }
    );

    const { refetch: loadServicesToExport, isLoading: exportIsLoading } = useQuery(
        ['loadServicesToExport'],
        () => getAll({ order, search, start_date: startDate, end_date: endDate, service_type_id: serviceType }),
        {
            onSuccess: response => setServicesToExport(onMapDataToExport(
                response.data.map((service: any) => {
                    return {
                        key: service.data.id,
                        ...service.data,
                        ...service.relationships,
                        operator_name: service?.relationships?.booking?.relationships?.operator?.data?.name || ''
                    }
                })
            )),
            refetchOnWindowFocus: false,
            enabled: false
        }
    );

    useQuery(
        ['loadColumns'],
        () => getColumns(),
        {
            onSuccess: response => {
                let selectOptions = !response.data.length
                    ? columns.filter((column: any) => !column.fixed).map((column: any) => column.key)
                    : response.data;

                formSelectColumns.setFieldValue('columns', selectOptions);
                setSelectColumns(selectOptions);
                setServicesToExport([]);
            },
            refetchOnWindowFocus: false
        }
    );

    const { mutate: updateSelectColumns } = useMutation(updateColumns, {
        onSuccess: () => {
            setSelectColumnsChanged(false);
            setServicesToExport([]);
        }
    });

    const { mutate: removeServiceMutate } = useMutation(remove,
        {
            onSuccess: () => {
                AlertService.sendAlert([
                    { text: `Serviço ${removeService.booking_id}-${removeService.id} apagado com sucesso.` }
                ]);

                setRemoveService(undefined);

                if (openServiceModalForm) {
                    onCloseServiceModalForm();
                }

                loadServices();
                setServicesToExport([]);
            },
            onError: () => {
                setRemoveService(undefined);

                if (openServiceModalForm) {
                    onCloseServiceModalForm();
                }
            }
        }
    );
    
    const { mutate: updateServiceVerifiedMutate } = useMutation(update, {
        onSuccess: (data: any) => {
            if(services && data && data.data) {
                let new_services: typeof services = [];
                
                let new_service: typeof services[0] = {
                    key: data.data.id,
                    ...data.data,
                    ...data.relationships,
                } satisfies typeof services[0];
                
                for(let service of services) {
                    new_services.push(
                        service.key === new_service.key
                            ? new_service
                            : service
                    );
                }
                
                setServices(new_services);
            } else {
                loadServices();
            }
        },
        onError: () => {}
    });
    
    

    const { data: serviceTypes } = useQuery(
        ['loadServiceTypes'],
        () => loadServiceTypes(),
        { refetchOnWindowFocus: false }
    );

    const { data: serviceStates } = useQuery(
        ['loadServiceStates'],
        () => loadServiceStates(),
        { refetchOnWindowFocus: false }
    );

    const { data: locations } = useQuery(
        ['loadLocations'],
        () => loadLocations(),
        { refetchOnWindowFocus: false }
    );

    const { data: zones } = useQuery(
        ['loadZones'],
        () => loadZones(),
        { refetchOnWindowFocus: false }
    );

    const { data: staff } = useQuery(
        ['loadStaff'],
        () => loadWorkers({ type: 'staff', status: 'active', with_relationship: '0' }),
        { refetchOnWindowFocus: false }
    );

    const { data: suppliers } = useQuery(
        ['loadSuppliers'],
        () => loadWorkers({ type: 'suppliers', status: 'active', with_relationship: '0' }),
        { refetchOnWindowFocus: false }
    );

    const { data: vehicles } = useQuery(
        ['loadVehicles'],
        () => loadVehicles(),
        { refetchOnWindowFocus: false }
    );
    
    const onFilterOptions = (
        input: string,
        option?: { key: string; value: number; children: string }
    ) => (option?.children || "").toLowerCase().includes(input.toLowerCase());

    const onOpenServiceModalForm = (title: string, record: any) => {
        setServiceToEdit(record);
        setModalFormTitle(title);
        setOpenServiceModalForm(true);
    };

    const onCloseServiceModalForm = () => {
        setOpenServiceModalForm(false);
        setServiceToEdit(undefined);
    };

    const onSavedForm = (successMessage: string, newRecord: any = null) => {
        onCloseServiceModalForm();

        setTimeout(() => {
            AlertService.sendAlert([
                { text: successMessage }
            ]);

            loadServices();
            setServicesToExport([]);
        }, 500);
    }

    const onExportDataToExcel = () => {
        if (servicesToExport.length) {
            downloadExcel(servicesToExport, 'Serviços');
        }
    };

    const onMapDataToExport = (data: Array<any>) => {
        return data.reduce((newData, service: any) => {
            newData.push(service);
            return newData;
        }, [])
            .map((service: any) => {
                return {
                    'Tipo de serviço': service?.serviceType?.data?.name,
                    ...columns.filter((column: any) =>
                        !column.fixed && column.key !== 'emphasis' && (
                            !selectColumns?.length
                            || selectColumns?.includes(column.key)
                        )
                    ).reduce((object, column) => {
                        object[column.title] = typeof column.renderToExport === 'function'
                            ? column.renderToExport(service)
                            : (typeof column.render === 'function'
                                ? column.render('', service)
                                : service[column.key]);

                        return object;
                    }, {})
                }
            })
    };

    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);

    const handleSearch = (selectedKeys: string[], confirm: () => void, dataIndex: string) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters?: () => void) => {
        if (clearFilters) clearFilters();
        setSearchText('');
    };

    const handleColumnFilter = (field: string, value: string) => {
        setCurrentPage(1);
        setColumnFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const getColumnSearchProps = (dataIndex: string, placeholder = 'Pesquisar') => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }: any) => (
            <div style={{ padding: 8 }} onKeyDown={e => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={placeholder}
                    value={selectedKeys[0]}
                    onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => {
                        confirm();
                        handleColumnFilter('search_' + dataIndex, selectedKeys[0]);
                        setSearchedColumn(dataIndex);
                    }}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => {
                            confirm();
                            handleColumnFilter('search_' + dataIndex, selectedKeys[0]);
                            setSearchedColumn(dataIndex);
                        }}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Pesquisar...
                    </Button>
                    <Button
                        onClick={() => handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Limpar
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => close()}
                    >
                        Fechar
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        render: (text: any, record: any) =>
            searchedColumn === dataIndex ? (
                <span style={{ backgroundColor: '#ffc069', padding: 0, borderRadius: 2 }}>{text}</span>
            ) : (
                text
            ),
    });

    const columns: any[] = [
        {
            title: 'Tipo de serviço', key: 'service_type_id', dataIndex: 'service_type_id', sorter: true, fixed: 'left', width: isMobile ? 100 : 130,
            render: (_: any, record: any) => (<Space>
                <Badge color={record?.serviceType?.data?.color} text={record?.serviceType?.data?.name} styles={{ indicator: { width: 12, height: 12 } }} />
            </Space>),
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Data', key: 'start', dataIndex: 'start', sorter: true, width: 130,
            render: (_: any, record: any) => record.start ? moment(record.start, 'YYYY-MM-DD').format('DD/MM/YYYY') : '',
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Hora', key: 'hour', dataIndex: 'hour', sorter: true, width: 80,
            render: (_: any, record: any) => record.hour ? moment(record.hour, 'H:mm:ss').format('HH:mm') : '',
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Tipo',
            key: 'tipo_motorista',
            width: 80,
            filters: [
                { text: 'Staff', value: 'S' },
                { text: 'Fornecedor', value: 'F' },
            ],
            filterMultiple: false,
            render: (_: any, record: any) => {
                if (record?.staff?.data?.name) return 'S';
                if (record?.supplier?.data?.name) return 'F';
                return '';
            },
            renderToExport: (record: any) => {
                if (record?.staff?.data?.name) return 'S';
                if (record?.supplier?.data?.name) return 'F';
                return '';
            },
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Motorista',
            key: 'driver',
            dataIndex: 'driver',
            width: 170,
            sorter: true,
            ...getColumnSearchProps('driver', 'Buscar motorista'),
            render: (_: any, record: any) => cutLongText(record?.staff?.data?.name || record?.supplier?.data?.name, 20),
            onFilter: (value: string, record: any) => {
                const nome = record?.staff?.data?.name || record?.supplier?.data?.name || '';
                return nome.toLowerCase().toLowerCase().includes((value as string).toLowerCase());
            },
            renderToExport: (record: any) => record?.staff?.data?.name || record?.supplier?.data?.name,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Alerta', key: 'emphasis', dataIndex: 'emphasis', width: 55,
            render: (_: any, record: any) => record?.emphasis === true
                ? (<Tooltip placement="top" title="Ação necessária">
                    <ExclamationCircleOutlined style={{ color: '#f70303ff' }} />
                </Tooltip>)
                : '',
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'ID Reserva', key: 'booking_id', dataIndex: 'booking_id', width: 100, sorter: true,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'ID Serviço', key: 'id', dataIndex: 'id', width: 100, sorter: true,
            render: (_: any, record: any) => `${record.booking_id}-${record?.id}`,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Referência', key: 'reference', dataIndex: ['booking', 'data', 'reference'], width: 100,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
            renderToExport: (record: any) => record?.booking?.data?.reference,
        },
        {
            title: 'Estado', key: 'service_state_id', dataIndex: 'service_state_id', width: 150, sorter: true,
            render: (_: any, record: any) => `${record.serviceState?.data?.name || ''}`,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Cliente',
            key: 'client_name',
            width: 150,
            dataIndex: 'client_name',
            sorter: true,
            ...getColumnSearchProps('client_name', 'Buscar cliente'),
            render: (_: any, record: any) => cutLongText(record?.booking?.data?.client_name, 20),
            renderToExport: (record: any) => record?.booking?.data?.client_name,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'N° Voo', key: 'flight_number', dataIndex: 'flight_number', width: 100, sorter: true,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Pick-up',
            key: 'pickup_location',
            width: 200,
            dataIndex: 'pickup_location',
            sorter: true,
            render: (_: any, record: any) => cutLongText(record?.pickup_location, 20),
            renderToExport: (record: any) => record?.pickup_location,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Drop-off',
            key: 'dropoff_location',
            width: 200,
            sorter: true,
            dataIndex: 'dropoff_location',
            render: (_: any, record: any) => (cutLongText(record?.dropoff_location, 20)),
            renderToExport: (record: any) => record?.dropoff_location,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'A', key: 'number_adults', dataIndex: 'number_adults', width: 50, sorter: true,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'C', key: 'number_children', dataIndex: 'number_children', width: 50, sorter: true,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Operador',
            key: 'operator',
            width: 230,
            dataIndex: 'operator_name',
            sorter: true,
            ...getColumnSearchProps('operator_name', 'Buscar Operador'),
            render: (_: any, record: any) => cutLongText(record?.operator_name, 20),
            renderToExport: (record: any) => record?.operator_name,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Obs. Gerais', key: 'notes', dataIndex: 'notes', width: 230,
            render: (_: any, record: any) => (cutLongText(record?.notes)),
            renderToExport: (record: any) => record?.notes,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Obs. Internas', key: 'internal_notes', width: 200,
            render: (_: any, record: any) => (cutLongText(record?.internal_notes)),
            renderToExport: (record: any) => record?.internal_notes,
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Val. Serviço', key: 'booking_value', dataIndex: 'value', width: 130, sorter: true,
            render: (_: any, record: any) => formatCurrency(record?.value || 0),
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Verificado',
            key: 'verified',
            dataIndex: 'verified',
            width: 100,
            sorter: true,
            render: (_: any, record: any) => (
                <Tooltip title={record.verified ? 'Marcar como não verificado' : 'Marcar como verificado'}>
                    <Space direction="horizontal">
                        <Switch
                            checked={record.verified}
                            style={{ marginRight: 10 }}
                            size="default"
                            onChange={(checked: boolean) => {
                                setChangeVerified({ value: checked, id: record.id });
                            }}
                        />
                    </Space>
                </Tooltip>
            ),
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        },
        {
            title: 'Ações', key: 'action', width: isMobile ? 90 : 165,
            render: (_: any, record: any) => (
                <GenericTable.Actions items={[
                    {
                        label: 'Ir para reserva',
                        icon: <LinkOutlined />,
                        linkTo: `/bookings/bookings/${record.booking_id}/edit`,
                    }, {
                        label: 'Editar',
                        icon: <EditOutlined />,
                        onClick: () => onOpenServiceModalForm(
                            settings.edit.title,
                            { ...record }
                        )
                    }, {
                        label: 'Apagar',
                        icon: <DeleteOutlined />,
                        onClick: () => setRemoveService(record)
                    }, {
                        label: 'Histórico do serviço',
                        icon: <ClockCircleOutlined />,
                        onClick: () => onOpenHistoric(record?.historic || [])
                    }
                ]} enabled={
                    profilePermissionService.hasPermission('bookings-services:write')
                } />
            ),
            renderToExport: (record: any) => {
                return '';
            },
            responsive: ['xs', 'sm', 'md', 'lg', 'xl'],
        }
    ];

    const formService = (
        <FormService bookingId={serviceToEdit?.booking_id} showBookingData={true}
            service={serviceToEdit} isParent={false}
            closeForm={onCloseServiceModalForm} savedForm={onSavedForm}
            setRemoveService={setRemoveService}
            serviceTypes={serviceTypes?.data?.map((item: any) => item.data) || []}
            serviceStates={serviceStates?.data?.map((item: any) => item.data) || []}
            staff={staff?.data || []} suppliers={suppliers?.data || []}
            vehicles={vehicles?.data?.map((item: any) => item.data) || []}
            locations={locations?.data || []}
            zones={zones?.data || []}
            operatorId={serviceToEdit?.booking?.data?.operator_id}
        />
    );

    const onSearch = (search_value: any) => {
        setCurrentPage(1);
        setSearch(search_value);
    };

    const onFilter = () => {
        setCurrentPage(1);
        let start_date = formFilter.getFieldValue('filter_start_date');
        let end_date = formFilter.getFieldValue('filter_end_date');

        setStartDate(start_date ? start_date.format('YYYY-MM-DD') : '');
        setEndDate(end_date ? end_date.format('YYYY-MM-DD') : '');
        setServiceType(formFilter.getFieldValue('filter_service_type'));
        setPickup(formFilter.getFieldValue('filter_pickup'));
        setDropoff(formFilter.getFieldValue('filter_dropoff'));
    };

    const onClearFilters = () => {
        setCurrentPage(1);
        setStartDate('');
        setEndDate('');
        setServiceType([]);
        setPickup(undefined);
        setDropoff(undefined);
    };

    const onOpenHistoric = (historic: any[]) => {
        setHistoric(historic);
        setOpenHistoric(true);
    };

    const onCloseHistoric = () => {
        setOpenHistoric(false);
        setHistoric([]);
    };

    const disabledStartDate: RangePickerProps['disabledDate'] = (current) => {
        let endDate = formFilter.getFieldValue('filter_end_date');

        if (endDate) {
            return current > endDate;
        }

        return false;
    };

    const disabledEndDate: RangePickerProps['disabledDate'] = (current) => {
        let startDate = formFilter.getFieldValue('filter_start_date');

        if (startDate) {
            return current < startDate;
        }

        return false;
    };

    const disabledExportPdfButtons = () => servicesToExport.length && servicesToExport.length <= limitServicesToExportPdf;

    const handleTableChange = (
        pagination: PaginationProps,
        filters: Record<string, FilterValue | null>,
        sorter: any
    ) => {
        if (pagination.current != currentPage) {
            setCurrentPage(pagination.current);
        }

        if (pagination.pageSize != perPage) {
            setPerPage(pagination.pageSize);
            setCurrentPage(1);
        }

        if (sorter.order) {
            let orderBy = `${sorter.field}-${sorter.order.substr(0, sorter.order.length - 3)}`;

            if (order != orderBy) {
                setOrder(orderBy);
            }
        }

        if (filters.tipo_motorista && filters.tipo_motorista.length) {
            // S (Staff) ou F (Fornecedor)
            let onlyWorkerType = filters.tipo_motorista[0] === 'S' ? 'STAFF' : 'SUPPLIERS';
            setColumnFilters(prev => ({
                ...prev,
                onlyWorkerType
            }));
        } else {
            // Remove o filtro se desmarcar tudo
            setColumnFilters(prev => {
                const clone = { ...prev };
                delete clone.onlyWorkerType;
                return clone;
            });
        };
    };

    const onChangeSelectColumns = () => {
        let selectedColumns = formSelectColumns.getFieldValue('columns');
        setSelectColumns(selectedColumns);
    }

    const onSubmitSelectColumns = () => {
        if (selectColumnsChanged) {
            updateSelectColumns(selectColumns);
        }
    }

    const allColumnsSelected = () => selectColumns.length == columns.filter((column: any) => !column.fixed).length;

    const filters = (
        <Form
            form={formFilter}
            name="filter_bookings"
            layout="vertical"
            className="form_filter_horizontal"
            autoComplete="off"
            style={isMobile ? {} : { width: 300 }}>
            <Row style={{ width: '100%' }} gutter={20}>
                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label="Tipo de serviço" name="filter_service_type" className="item_filter">
                        <Select placeholder="Todos" size="large" mode="multiple" value={serviceType}
                            style={isMobile ? {} : { width: '100%' }}>
                            {(serviceTypes?.data || []).map((item: any) => (
                                <Select.Option key={item.data.id} value={item.data.id}>
                                    <Badge color={item.data.color} text={item.data.name} styles={{ indicator: { width: 12, height: 12 } }} />
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label="Data início" name="filter_start_date" className="item_filter">
                        <DatePicker size="large" placeholder="dd/mm/aaaa" showToday={false}
                            format={'DD/MM/YYYY'} disabledDate={disabledStartDate} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label="Data fim" name="filter_end_date" className="item_filter">
                        <DatePicker size="large" placeholder="dd/mm/aaaa" showToday={false}
                            format={'DD/MM/YYYY'} disabledDate={disabledEndDate} style={{ width: '100%' }} />
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label="Pick-up" name="filter_pickup" className="item_filter">
                        <Select showSearch allowClear placeholder="Selecione um local" size="large" filterOption={onFilterOptions}>
                            {(locations?.data || []).map(({data: location}: {data: any}) => (
                                <Select.Option key={location.id} value={location.id}>{location.name}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Form.Item label="Drop-off" name="filter_dropoff" className="item_filter">
                        <Select showSearch allowClear placeholder="Selecione um local" size="large" filterOption={onFilterOptions}>
                            {(locations?.data || []).map(({data: location}: {data: any}) => (
                                <Select.Option key={location.id} value={location.id}>{location.name}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Col>

                <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 24 }} xl={{ span: 24 }}>
                    <Button onClick={onFilter} size="large">Filtrar</Button>

                    {(startDate || endDate || (serviceType && serviceType.length > 0) || pickup || dropoff)
                        && <Button type="link" onClick={onClearFilters} size="large">Limpar</Button>
                    }
                </Col>
            </Row>
        </Form>
    );

    useEffect(() => {
        formFilter.setFieldsValue({
            filter_start_date: dayjs(startDate),
            filter_end_date: dayjs(endDate),
            filter_service_type: [],
            filter_pickup: undefined,
            filter_dropoff: undefined,
        });
    }, []);

    useEffect(() => {
        if (!startDate) {
            formFilter.setFieldValue('filter_start_date', '');
        }

        if (!endDate) {
            formFilter.setFieldValue('filter_end_date', '');
        }

        formFilter.setFieldValue('filter_service_type', serviceType);
        formFilter.setFieldValue('filter_pickup', pickup);
        formFilter.setFieldValue('filter_dropoff', dropoff);
    }, [startDate, endDate, serviceType, pickup, dropoff]);

    useEffect(() => {
        if (servicesToExport.length) {
            updatePdfInstante(<ExportServicesPDF
                services={servicesToExport}
                filters={{ start_date: startDate, end_date: endDate }} />
            );

            if (servicesToExport.length <= limitServicesToExportPdf) {
                updatePdfInstante(<ExportServicesPDF
                    services={servicesToExport}
                    filters={{ start_date: startDate, end_date: endDate }} />
                );
            } else {
                updatePdfInstante(<ExportServicesPDF
                    services={[]}
                    filters={{ start_date: startDate, end_date: endDate }} />
                );
            }

            setOpenExportOptions(true);
        }

    }, [servicesToExport]);

    
    useEffect(() => {
        if (changeVerified) {
            const obj = {
                values: {
                    verified: changeVerified.value
                },
                id: changeVerified.id,
            };
            
            updateServiceVerifiedMutate(obj);
        }
    }, [changeVerified]);
    
    
    return (
        <>
            <DynamicServiceColors dataSource={services} />

            <div className="content_page">
                {!openServiceModalForm && <DisplayAlert />}
                <Card bodyStyle={{ padding: 8 }}>
                    <div className="search_bar">
                        <SearchInput
                            form={formSearch}
                            name="search_bookings"
                            buttonText="Pesquisar"
                            placeholder="Pesquisa"
                            onSearch={onSearch}
                        />
                    </div>

                    <div className="table">
                        <GenericTable
                            columns={columns.filter((column: any) =>
                                !selectColumns?.length
                                || (column.fixed || selectColumns?.includes(column.key))
                            )}
                            rowClassName={(record: any) => {
                                const color = record?.serviceType?.data?.color;
                                return color ? `service-type-color-${color.replace('#', '')}` : '';
                            }}
                            dataSource={services}
                            pagination={{
                                current: currentPage,
                                pageSize: perPage,
                                showSizeChanger: true,
                                total: total
                            }}
                            loading={servicesLoading}
                            scroll={{ x: 1100 }}
                            size='small'
                            totalChildren={
                                <div className="" style={isMobile ? {} : { marginTop: 8 }}>
                                    <Row>
                                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 12 }}
                                            style={isMobile ? { marginBottom: 10 } : {}}>
                                            <ShowDataLength className="show_result_size" size={total} />
                                        </Col>

                                        <Col xs={{ span: 24 }} sm={{ span: 24 }} md={{ span: 24 }} lg={{ span: 12 }} xl={{ span: 12 }}
                                            style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <Space className="show_result_size" direction={isMobile ? 'vertical' : 'horizontal'}>
                                                <Form layout='vertical' form={formSelectColumns} onValuesChange={() => setSelectColumnsChanged(true)}>
                                                    <Form.Item name="columns">
                                                        <Select placeholder="Todas as colunas" mode="multiple" placement="bottomRight"
                                                            maxTagCount={0} maxTagPlaceholder={(omittedValues) => {
                                                                let label = allColumnsSelected()
                                                                    ? 'Todas as colunas'
                                                                    : 'Personalizado';

                                                                return (
                                                                    <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                                                                        <span>{label}</span>
                                                                    </Tooltip>
                                                                );
                                                            }}
                                                            dropdownRender={(menu) => (<>
                                                                <Button block type='text' style={{
                                                                    display: 'flex', justifyContent: 'space-between',
                                                                    backgroundColor: allColumnsSelected() ? '#d1fdff' : ''
                                                                }}
                                                                    disabled={allColumnsSelected()}
                                                                    onClick={() => {
                                                                        let selectOptions = columns
                                                                            .filter((column: any) => !column.fixed)
                                                                            .map((column: any) => column.key);

                                                                        formSelectColumns.setFieldValue('columns', selectOptions);
                                                                        setSelectColumns(selectOptions);
                                                                        setSelectColumnsChanged(true);
                                                                    }}>
                                                                    Todas as colunas
                                                                    {allColumnsSelected() ? <CheckOutlined style={{ color: '#107b99', marginTop: 3 }} /> : ''}
                                                                </Button>
                                                                {menu}
                                                            </>)}
                                                            onChange={onChangeSelectColumns}
                                                            onBlur={onSubmitSelectColumns}
                                                            value={selectColumns}
                                                            style={{ width: isMobile ? 300 : 250 }}
                                                            options={columns.filter((column: any) => !column.fixed)
                                                                .map((item: any) => {
                                                                    return {
                                                                        key: item.key, value: item.key, label: item.title
                                                                    }
                                                                })} />
                                                    </Form.Item>
                                                </Form>

                                                <FilterButton filters={filters} style={{ marginBottom: 19 }} />

                                                <ExportBtn disabled={!total}
                                                    loading={exportIsLoading || pdfInstante.loading}
                                                    isDropdown={true}
                                                    open={openExportOptions}
                                                    block={isMobile}
                                                    onClose={() => setOpenExportOptions(false)}
                                                    onClick={() => {
                                                        setOpenExportOptions(true);

                                                        if (!servicesToExport.length) {
                                                            loadServicesToExport();
                                                        }
                                                    }}
                                                    items={<Row style={{ marginTop: 40 }}>
                                                        <Space size="small" direction={isMobile ? 'vertical' : 'horizontal'}>
                                                            <Button type="link" icon={<FileExcelOutlined />} style={colorBtns}
                                                                loading={exportIsLoading || pdfInstante.loading} onClick={onExportDataToExcel}>
                                                                Exportar para Excel</Button>
                                                            <Tooltip placement="top" title={!disabledExportPdfButtons() ? "Filtro superior ao limite de 300 serviços" : ''}>
                                                                <Button type="link" icon={<FilePdfOutlined />} style={!disabledExportPdfButtons() ? colorBtnsDisabled : colorBtns}
                                                                    href={pdfInstante.url || '#'} download="Serviços.pdf"
                                                                    disabled={!disabledExportPdfButtons() ? true : false}
                                                                    loading={exportIsLoading || pdfInstante.loading}>
                                                                    Guardar como PDF</Button>

                                                                <Button type="link" icon={<PrinterOutlined />} style={!disabledExportPdfButtons() ? colorBtnsDisabled : colorBtns}
                                                                    href={pdfInstante.url || '#'} target="_blank"
                                                                    disabled={!disabledExportPdfButtons() ? true : false}
                                                                    loading={exportIsLoading || pdfInstante.loading}>
                                                                    Imprimir
                                                                </Button>
                                                            </Tooltip>
                                                        </Space>
                                                    </Row>} />
                                            </Space>
                                        </Col>
                                    </Row>
                                </div>
                            }
                            handleTableChange={handleTableChange}
                        />
                    </div>

                    <Modal
                        open={openServiceModalForm} width={1180} style={{ top: 20 }}
                        footer={null} maskClosable={false} closeIcon={false}>
                        <DisplayAlert />
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Text strong style={{ marginTop: 10 }}>{modalFormTitle}</Text>
                        </div>

                        <Divider style={{ marginBottom: 0 }}></Divider>

                        {formService}
                    </Modal>

                    <ConfirmModal
                        open={!!removeService} zIndex={2000}
                        title={`Tem certeza que deseja apagar o serviço "${removeService?.booking_id}-${removeService?.id}"?`}
                        okText="Apagar" cancelText="Voltar"
                        onOk={() => removeServiceMutate(removeService?.id)}
                        onCancel={() => setRemoveService(undefined)}
                    ></ConfirmModal>

                    <Drawer
                        title="Histórico da reserva" placement="right" width={450}
                        closeIcon={false} open={openHistoric} onClose={onCloseHistoric}
                        extra={<Button type="text" shape="circle" size="middle" icon={<CloseOutlined />}
                            onClick={onCloseHistoric} />}>
                        {historic && <ShowHistoric historic={historic} />}
                    </Drawer>
                </Card >
            </div >
        </>
    )
}